import {
  CreateFinancialEntryPayload,
  FinancialEntryDetail,
  UpdateFinancialEntryPayload,
} from './types';
import {
  defaultFormSchemaValues,
  FinancialEntryFormInstallment,
  FinancialEntryFormItem,
  FinancialEntryFormSchemaType,
} from './FinancialEntryForm/schema';
import {
  isItemFieldComplete,
  isInstallmentComplete,
} from './FinancialEntryForm/formValidation';
import { FINANCIAL_ENTRY_RECURRENCE, FINANCIAL_ENTRY_STATUS } from './constants';
import { parseCurrencyValue, parsePercentValue } from './financialFormatters';

const parseNumber = (value: string | number | null | undefined): number | undefined =>
  parseCurrencyValue(value);

const toIsoDate = (value: string | null | undefined): string | undefined => {
  if (!value) return undefined;
  return value.trim().slice(0, 10);
};

export const computeTotalValue = (installments: FinancialEntryFormInstallment[]): number => {
  return installments.reduce((sum, inst) => sum + (parseNumber(inst.value) ?? 0), 0);
};

export const formatInstallmentPosition = (index: number, total: number): string =>
  `${index + 1}/${total}`;

const RECURRENCE_MONTHS: Record<number, number> = {
  [FINANCIAL_ENTRY_RECURRENCE.MONTHLY]: 1,
  [FINANCIAL_ENTRY_RECURRENCE.QUARTERLY]: 3,
  [FINANCIAL_ENTRY_RECURRENCE.SEMIANNUAL]: 6,
  [FINANCIAL_ENTRY_RECURRENCE.YEARLY]: 12,
};

const parseLocalDate = (dateStr: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
};

const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const addMonths = (date: Date, months: number): Date => {
  const result = new Date(date);
  const day = result.getDate();
  result.setMonth(result.getMonth() + months);

  if (result.getDate() !== day) {
    result.setDate(0);
  }

  return result;
};

export const computeInstallmentDueDates = (
  firstDueDate: string,
  recurrence: number | null | undefined,
  count: number,
): string[] | null => {
  if (!firstDueDate || count <= 0) return null;

  const monthsStep = recurrence != null ? RECURRENCE_MONTHS[recurrence] : undefined;
  if (monthsStep === undefined) return null;

  const baseDate = parseLocalDate(firstDueDate);
  if (!baseDate) return null;

  return Array.from({ length: count }, (_, index) =>
    index === 0 ? firstDueDate : formatLocalDate(addMonths(baseDate, monthsStep * index)),
  );
};

export const getInstallmentDisplayLabel = (
  installmentNumber: string | undefined | null,
  index: number,
  total: number,
): string => {
  const trimmed = installmentNumber?.trim();
  if (trimmed) return trimmed;
  return formatInstallmentPosition(index, total);
};

export const formatInstallmentLabel = (
  installment: FinancialEntryFormInstallment,
  index: number,
): string => {
  const number =
    installment.installmentNumber?.trim() ||
    formatInstallmentPosition(index, 1);
  const value = parseNumber(installment.value);
  const formattedValue =
    value !== undefined
      ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
      : '—';
  return `Parcela ${number} — ${formattedValue}`;
};

const mapInstallmentToPayload = (
  inst: FinancialEntryFormInstallment,
  index: number,
  total: number,
) => ({
  ...(inst.id ? { id: inst.id } : {}),
  value: parseNumber(inst.value),
  dueDate: toIsoDate(inst.dueDate),
  commission: parsePercentValue(inst.commission),
  installmentNumber:
    inst.installmentNumber?.trim() || formatInstallmentPosition(index, total),
});

const mapItemToPayload = (item: FinancialEntryFormItem) => {
  const attachmentIds = (item.attachments ?? [])
    .map((attachment) => attachment.id)
    .filter((id): id is number => typeof id === 'number' && id > 0);

  return {
    ...(item.id ? { id: item.id } : {}),
    businessUnit: item.businessUnit?.trim() || undefined,
    project: item.project?.trim() || undefined,
    competenceValue: parseNumber(item.competenceValue),
    competenceDate: toIsoDate(item.competenceDate),
    ...(attachmentIds.length > 0 ? { generalFileIds: attachmentIds } : {}),
  };
};

export const isItemMeaningful = (item: FinancialEntryFormItem): boolean =>
  isItemFieldComplete(item);

export const getMeaningfulFormItems = (
  items: FinancialEntryFormSchemaType['items'],
): FinancialEntryFormItem[] => normalizeItems(items).filter(isItemFieldComplete);

const getCompleteInstallments = (
  installments: FinancialEntryFormSchemaType['installments'],
): FinancialEntryFormInstallment[] =>
  normalizeInstallments(installments).filter(isInstallmentComplete);

const normalizeInstallments = (
  installments: FinancialEntryFormSchemaType['installments'],
): FinancialEntryFormInstallment[] =>
  (installments ?? []).map((inst) => ({
    id: inst.id,
    value: inst.value ?? '',
    dueDate: inst.dueDate ?? '',
    commission: inst.commission ?? '',
    installmentNumber: inst.installmentNumber ?? '',
  }));

const normalizeItems = (
  items: FinancialEntryFormSchemaType['items'],
): FinancialEntryFormItem[] =>
  (items ?? []).map((item) => ({
    id: item.id,
    businessUnit: item.businessUnit ?? '',
    project: item.project ?? '',
    competenceValue: item.competenceValue ?? '',
    competenceDate: item.competenceDate ?? '',
    attachments: item.attachments ?? [],
    pendingFiles: item.pendingFiles ?? [],
  }));

export const formToSplitCreatePayload = (
  form: FinancialEntryFormSchemaType,
): CreateFinancialEntryPayload => {
  const installments = getCompleteInstallments(form.installments);
  const items = getMeaningfulFormItems(form.items);
  const totalValue = computeTotalValue(installments);

  return {
    companyId: form.companyId ?? undefined,
    linkedUserId: form.linkedUserId ?? undefined,
    type: form.type ?? undefined,
    category: form.category ?? undefined,
    status: form.status ?? FINANCIAL_ENTRY_STATUS.BUDGETED,
    recurrence: form.recurrence ?? undefined,
    description: form.description?.trim() || undefined,
    paymentMethodId: form.paymentMethodId ?? undefined,
    totalValue,
    freight: parseNumber(form.freight),
    icms: parseNumber(form.icms),
    ipi: parseNumber(form.ipi),
    installments: installments.map((inst, index) => ({
      value: parseNumber(inst.value),
      dueDate: toIsoDate(inst.dueDate),
      commission: parsePercentValue(inst.commission),
      installmentNumber:
        inst.installmentNumber?.trim() || formatInstallmentPosition(index, installments.length),
    })),
    ...(items.length > 0 ? { items: items.map(mapItemToPayload) } : {}),
  };
};

export const formToUpdatePayload = (
  form: FinancialEntryFormSchemaType,
): UpdateFinancialEntryPayload => {
  const installments = getCompleteInstallments(form.installments);
  const items = getMeaningfulFormItems(form.items);
  const totalValue = computeTotalValue(installments);

  return {
    companyId: form.companyId,
    linkedUserId: form.linkedUserId,
    type: form.type ?? undefined,
    category: form.category ?? undefined,
    status: form.status ?? undefined,
    recurrence: form.recurrence ?? undefined,
    description: form.description?.trim() || undefined,
    paymentMethodId: form.paymentMethodId ?? undefined,
    totalValue,
    freight: parseNumber(form.freight),
    icms: parseNumber(form.icms),
    ipi: parseNumber(form.ipi),
    installments: installments.map((inst, index) =>
      mapInstallmentToPayload(inst, index, installments.length),
    ),
    items: items.map(mapItemToPayload),
  };
};

const emptyInstallment = (): FinancialEntryFormInstallment => ({
  ...defaultFormSchemaValues.installments[0],
});

const emptyItem = (): FinancialEntryFormItem => ({
  ...defaultFormSchemaValues.items![0],
  attachments: [],
  pendingFiles: [],
});

export const detailToForm = (detail: FinancialEntryDetail): FinancialEntryFormSchemaType => ({
  companyId: detail.companyId,
  companyName: detail.company?.companyName ?? '',
  linkedUserId: detail.linkedUserId,
  linkedUserName: detail.linkedUser?.name ?? '',
  type: detail.type,
  category: detail.category,
  status: detail.status,
  recurrence: detail.recurrence,
  description: detail.description ?? '',
  paymentMethodId: detail.paymentMethodId,
  freight: detail.freight != null ? String(detail.freight) : '',
  icms: detail.icms != null ? String(detail.icms) : '',
  ipi: detail.ipi != null ? String(detail.ipi) : '',
  installments:
    detail.installments.length > 0
      ? detail.installments.map((inst) => ({
          id: inst.id,
          value: inst.value != null ? String(inst.value) : '',
          dueDate: inst.dueDate ? inst.dueDate.slice(0, 10) : '',
          commission: inst.commission != null ? String(inst.commission) : '',
          hasCommission: inst.commission != null && Number(inst.commission) > 0,
          installmentNumber: inst.installmentNumber ?? '',
        }))
      : [emptyInstallment()],
  items:
    detail.items.length > 0
      ? detail.items.map((item) => ({
          id: item.id,
          businessUnit: item.businessUnit ?? '',
          project: item.project ?? '',
          competenceValue: item.competenceValue != null ? String(item.competenceValue) : '',
          competenceDate: item.competenceDate ? item.competenceDate.slice(0, 10) : '',
          attachments: item.attachments ?? [],
          pendingFiles: [],
        }))
      : [emptyItem()],
});
